import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { SalesTransaction } from '@/api/models';
import { TransactionsView } from './TransactionsView';
import type { TransactionsFilterState } from './transactionsTypes';
import { useTransactions } from './useTransactions';

type TransactionsContentProps = TransactionsFilterState & {
    error: ErrorResponse | null;
    hasMore: boolean;
    isFetchingMore: boolean;
    isLoading: boolean;
    items: SalesTransaction[];
    onLoadMore: () => void;
    totalCount: number;
};

/**
 * Self-served Transactions widget. Owns its own data hook and is
 * wrapped in an `ErrorBoundary` so a render-time failure cannot crash
 * the Sales page. The hook returns the initial-load + load-more state;
 * a transient failure on the next page does not blank the rows already
 * on screen.
 */
export const Transactions: React.FC = () => {
    const {
        discountedOnly,
        setDiscountedOnly,
        error,
        hasMore,
        isFetchingMore,
        isLoading,
        items,
        onLoadMore,
        onRetry,
        search,
        setSearch,
        setTransactionType,
        totalCount,
        transactionType,
    } = useTransactions();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('sales.widgets.transactions.errorTitle'),
            errorMessage: t('sales.widgets.transactions.errorMessage'),
            errorRetry: t('sales.widgets.transactions.errorRetry'),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid="transactions-error-boundary"
            fallbackMessage={copy.errorMessage}
            id="transactions-error-boundary"
            onRetry={onRetry}
            resetKeys={[error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <TransactionsContent
                discountedOnly={discountedOnly}
                setDiscountedOnly={setDiscountedOnly}
                error={error}
                hasMore={hasMore}
                isFetchingMore={isFetchingMore}
                isLoading={isLoading}
                items={items}
                onLoadMore={onLoadMore}
                search={search}
                setSearch={setSearch}
                setTransactionType={setTransactionType}
                totalCount={totalCount}
                transactionType={transactionType}
            />
        </ErrorBoundary>
    );
};

const TransactionsContent: React.FC<TransactionsContentProps> = ({
    discountedOnly,
    setDiscountedOnly,
    error,
    hasMore,
    isFetchingMore,
    isLoading,
    items,
    onLoadMore,
    search,
    setSearch,
    setTransactionType,
    totalCount,
    transactionType,
}) => {
    // Only throw on the *initial* error — once we have rows on screen,
    // we keep them visible and surface the next-page failure through
    // the load-more sentinel instead of wiping the table.
    if (error && items.length === 0) {
        throw error;
    }

    return (
        <TransactionsView
            isLoading={isLoading && items.length === 0}
            discountedOnly={discountedOnly}
            setDiscountedOnly={setDiscountedOnly}
            hasMore={hasMore}
            isFetchingMore={isFetchingMore}
            items={items}
            onLoadMore={onLoadMore}
            search={search}
            setSearch={setSearch}
            setTransactionType={setTransactionType}
            totalCount={totalCount}
            transactionType={transactionType}
        />
    );
};
